/**
 * Portion display name → the published `/parashah/:slug`.
 *
 * The URL segment is owned by the `parashahs` datastore collection, whose
 * `slug` does not always match a slug of the display name: `Chayei Sarah` is
 * stored as `chayei-sara`, `Shemini` as `shmini`, `Shelach` as `shlach`,
 * `Behaalotecha` as `behaalotcha`. Deriving the link from the name (or keeping a
 * hand-written alias per exception) breaks the weekly portion link every time
 * another spelling drifts, so portions are matched by their **position in their
 * book** instead — a name-independent key shared by `portions.json` and the
 * collection.
 */

/** A row from the `parashahs` collection (only the fields the link needs). */
export interface ParashahSlugRow {
	_id?: string
	slug?: string
	bookId?: string
	orderInBook?: number | string
}

/** A row from the `books` collection. */
export interface BookRow {
	_id?: string
	id?: string
	name?: string
}

/** Minimal shape of a `portions.json` entry, enough to key it. */
export interface PortionLike {
	name: string
	book: string
}

/**
 * Position key for every portion: `"Genesis|5"` (book name + 1-based order).
 *
 * Built from `portions.json`, whose entries are already in reading order, so
 * the same key can be derived from the datastore's `bookId` + `orderInBook`.
 */
export function portionOrderKeys(portions: PortionLike[]): Map<string, string> {
	const orderByBook = new Map<string, number>()
	const byName = new Map<string, string>()
	for (const portion of portions) {
		const order = (orderByBook.get(portion.book) ?? 0) + 1
		orderByBook.set(portion.book, order)
		byName.set(portion.name, `${portion.book}|${order}`)
	}
	return byName
}

/**
 * Index the published parashah slugs by position key.
 *
 * Combined ("double") readings are stored as extra rows without `orderInBook`
 * and are skipped — they are schedule variants, not additional portions.
 */
export function parashahSlugIndex(
	parashahs: ParashahSlugRow[],
	books: BookRow[],
): Map<string, string> {
	const bookNameById = new Map<string, string>()
	for (const book of books) {
		const id = book._id ?? book.id
		if (id && book.name) bookNameById.set(id, book.name)
	}

	const index = new Map<string, string>()
	for (const parashah of parashahs) {
		const book = bookNameById.get(String(parashah.bookId ?? ''))
		const order = Number(parashah.orderInBook)
		if (!book || !parashah.slug || !Number.isInteger(order)) continue
		index.set(`${book}|${order}`, parashah.slug)
	}
	return index
}
